import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import { useMemo, useRef, useState, useEffect } from 'react';
import { Row, Col, Container } from '@paypalcorp/pp-react-grid';
import { devWarning, utilStyles, uniqueId, KeyboardCode } from '@paypalcorp/pp-react-utils';
import { CriticalIcon, WarningIcon, CausesIcon, ArrowLeftIcon, ArrowRightIcon } from '@paypalcorp/pp-react-icons';
import { css, useTheme, jsx } from '@emotion/react';
import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { textStyles } from '@paypalcorp/pp-react-text';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8;
function styles(tokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysBorderThicknessNormal = tokens.sysBorderThicknessNormal,
      sysSpaceTiny = tokens.sysSpaceTiny,
      sysSpaceSm = tokens.sysSpaceSm,
      sysColorErrorMain = tokens.sysColorErrorMain,
      sysColorErrorContrast = tokens.sysColorErrorContrast,
      sysColorErrorMainActive = tokens.sysColorErrorMainActive,
      sysColorWarningMain = tokens.sysColorWarningMain,
      sysColorWarningMainActive = tokens.sysColorWarningMainActive,
      sysColorWarningContrast = tokens.sysColorWarningContrast,
      sysColorSpecialtyMain = tokens.sysColorSpecialtyMain,
      sysColorSpecialtyMainActive = tokens.sysColorSpecialtyMainActive,
      sysColorSpecialtyContrast = tokens.sysColorSpecialtyContrast,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceJumbo = tokens.sysSpaceJumbo,
      sysSpaceMd = tokens.sysSpaceMd;

  var _utilStyles = utilStyles(tokens),
      focusStyles = _utilStyles.focusStyles;

  var _textStyles = textStyles(tokens),
      textBody = _textStyles.body,
      textTitle = _textStyles.title;
  /**
   * Get the color based on the type of banner
   * @param type string
   * @returns object
   *
   */


  var getColorMap = function (type) {
    var bgColor = sysColorSpecialtyMain;
    var activeBgColor = sysColorSpecialtyMainActive;
    var textColor = sysColorSpecialtyContrast;

    if (type === 'critical') {
      bgColor = sysColorErrorMain;
      activeBgColor = sysColorErrorMainActive;
      textColor = sysColorErrorContrast;
    } else if (type === 'warning') {
      bgColor = sysColorWarningMain;
      activeBgColor = sysColorWarningMainActive;
      textColor = sysColorWarningContrast;
    }

    return {
      bgColor: bgColor,
      activeBgColor: activeBgColor,
      textColor: textColor
    };
  };
  /**
   * Get the styles for the banner
   * @param type string
   * @returns SerializedStyles
   *
   */


  var getBanner = function (type) {
    var _getColorMap = getColorMap(type),
        bgColor = _getColorMap.bgColor,
        activeBgColor = _getColorMap.activeBgColor;

    return css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n      label: ", ";\n      background-color: ", ";\n      &:active {\n        background-color: ", ";\n      }\n    "])), type, bgColor, activeBgColor);
  };
  /**
   * Get the styles for the banner text
   * @param type string
   * @returns SerializedStyles
   *
   */


  var getTextColor = function (type) {
    var _getColorMap2 = getColorMap(type),
        textColor = _getColorMap2.textColor;

    var label = "text_".concat(type);
    return css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n      label: ", ";\n      color: ", ";\n    "])), label, textColor);
  }; // Base Class for banner


  var base = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: banner_base;\n    box-sizing: border-box;\n    padding: ", " ", ";\n    border-width: ", ";\n    position: relative;\n    width: 100%;\n    cursor: pointer;\n    display: block;\n    text-decoration: none;\n    outline: ", " solid transparent;\n    &:focus {\n      ", "\n    }\n  "])), sysSpaceMd, sysSpaceLg, sysBorderThicknessNormal, sysBorderThicknessNormal, focusStyles.focusInlineElement);

  var fullWidth = function (type) {
    var _getColorMap3 = getColorMap(type),
        bgColor = _getColorMap3.bgColor;

    return css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n      label: banner_full_width;\n      position: relative;\n      &:before {\n        content: '';\n        position: absolute;\n        background-color: ", ";\n        margin: 0 -50vw;\n        inset: 0 50%;\n      }\n    "])), bgColor);
  };

  var title = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: banner_title;\n    ", ";\n    text-align: left;\n    max-width: 100%;\n    width: 100%;\n    display: flex;\n    [dir='rtl'] & {\n      text-align: right;\n    }\n  "])), textTitle());
  var leftSideIcon = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: icon_left;\n    margin-right: ", ";\n    margin-top: ", ";\n    [dir='rtl'] & {\n      margin-left: ", ";\n      margin-right: 0;\n    }\n  "])), sysSpaceSm, sysSpaceTiny, sysSpaceSm);
  var rightSideIcon = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: icon_right;\n    margin-right: 0;\n    margin-left: auto;\n    margin-top: ", ";\n    [dir='rtl'] & {\n      margin-left: 0;\n      margin-right: auto;\n    }\n  "])), sysSpaceTiny);
  var subText = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: subtext;\n    ", "\n    display: flex;\n    padding: 0 ", ";\n    text-align: left;\n    [dir='rtl'] & {\n      margin-right: 0;\n      text-align: right;\n    }\n  "])), textBody(), sysSpaceJumbo);
  return {
    getBanner: getBanner,
    getTextColor: getTextColor,
    base: base,
    fullWidth: fullWidth,
    title: title,
    leftSideIcon: leftSideIcon,
    rightSideIcon: rightSideIcon,
    subText: subText
  };
}

var _excluded = ["id", "className", "titleClassName", "subTitleClassName", "title", "type", "specialtyIcon", "subText", "href", "fullWidth", "onClick"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var Banner = function (_ref) {
  var _styles$getBanner, _styles$getTextColor, _styles$getTextColor2, _styles$fullWidth;

  var _ref$id = _ref.id,
      id = _ref$id === void 0 ? uniqueId('Banner_') : _ref$id,
      className = _ref.className,
      titleClassName = _ref.titleClassName,
      subTitleClassName = _ref.subTitleClassName,
      _ref$title = _ref.title,
      title = _ref$title === void 0 ? 'default' : _ref$title,
      _ref$type = _ref.type,
      type = _ref$type === void 0 ? 'critical' : _ref$type,
      specialtyIcon = _ref.specialtyIcon,
      subText = _ref.subText,
      href = _ref.href,
      fullWidth = _ref.fullWidth,
      onClick = _ref.onClick,
      rest = _objectWithoutProperties(_ref, _excluded);

  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var bannerCssVal = [_styles.base, type && ((_styles$getBanner = _styles.getBanner) === null || _styles$getBanner === void 0 ? void 0 : _styles$getBanner.call(_styles, type))];
  var titleCssVal = [_styles.title, type && ((_styles$getTextColor = _styles.getTextColor) === null || _styles$getTextColor === void 0 ? void 0 : _styles$getTextColor.call(_styles, type))];
  var subTextCssVal = [_styles.subText, type && ((_styles$getTextColor2 = _styles.getTextColor) === null || _styles$getTextColor2 === void 0 ? void 0 : _styles$getTextColor2.call(_styles, type))];
  var subTextContent = subText && jsx("div", {
    css: subTextCssVal,
    className: subTitleClassName,
    "data-ppui": "true"
  }, ' ', subText);
  var titleRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      rtl = _useState2[0],
      setRtl = _useState2[1];

  useEffect(function () {
    var titleElm = titleRef.current;
    var computedStyle = window.getComputedStyle(titleElm);
    setRtl(computedStyle && computedStyle.direction === 'rtl');
  }, []);
  var StatusIcon = {
    critical: CriticalIcon,
    warning: WarningIcon,
    specialty: specialtyIcon || CausesIcon
  }[type];
  /* istanbul ignore next */

  var ClickIcon = rtl ? ArrowLeftIcon : ArrowRightIcon;
  var bannerContent = jsx(Row, {
    "data-ppui": "true"
  }, jsx(Col, {
    lg: 12,
    "data-ppui": "true"
  }, jsx("div", {
    css: titleCssVal,
    className: titleClassName,
    ref: titleRef,
    "data-ppui": "true"
  }, jsx(StatusIcon, {
    css: _styles.leftSideIcon,
    size: "sm",
    "data-ppui": "true"
  }), title, jsx(ClickIcon, {
    css: _styles.rightSideIcon,
    size: "sm",
    "data-ppui": "true"
  })), subTextContent));
  var commonProps = {
    className: className,
    css: bannerCssVal,
    'data-ppui-info': ''
  };

  var componentTypeProps = _objectSpread({
    id: id,
    tabIndex: 0,
    role: 'button',
    onClick: onClick,
    onKeyDown: function keyHandler(event) {
      /* istanbul ignore next */
      var KeyCode = new KeyboardCode(event.key || event.keyCode);

      if (KeyCode.IS_ENTER || KeyCode.IS_SPACEBAR) {
        event.preventDefault();
        onClick(event);
      }
    }
  }, commonProps);

  if (href) {
    componentTypeProps = _objectSpread({
      as: 'a',
      href: href
    }, commonProps);
  }

  var containerElm = jsx(Container, _extends({}, componentTypeProps, rest, {
    "data-ppui": "true"
  }), bannerContent);
  return fullWidth ? jsx("div", {
    css: (_styles$fullWidth = _styles.fullWidth) === null || _styles$fullWidth === void 0 ? void 0 : _styles$fullWidth.call(_styles, type),
    "data-ppui": "true"
  }, containerElm) : containerElm;
};

export { Banner, styles as bannerStyles };
